import React, { useState } from 'react'
import Title from './Title';
import { FaUser } from "react-icons/fa";
import { MdEmail } from "react-icons/md";
import Button from './Button';
import Bubbles from './Bubbles';

const FORMSPREE_ID = 'xdaqpwwn'; // Replace with your Formspree form ID

const initialState = {
  user_name: '',
  user_email: '',
  user_message: '',
}

const Contact = ({ title = true }) => {
  const [info, setInfo] = useState(initialState)
  const [focusedField, setFocusedField] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const sendMessage = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: info.user_name,
          email: info.user_email,
          message: info.user_message,
        }),
      });
      if (response.ok) {
        setSubmitted(true);
        setInfo({ user_name: '', user_email: '', user_message: '' });
        setTimeout(() => setSubmitted(false), 3000);
      } else {
        alert('Failed to send message. Please try again.');
      }
    } catch (error) {
      alert('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className={`relative bg-primary-bg min-h-[95vh] md:min-h-[71vh] overflow-auto lg:overflow-hidden flex items-center justify-center ${title ? 'py-24' : ''}`}
    >
      <div className="absolute inset-0 grid-pattern pointer-events-none opacity-20" />
      <Bubbles count={6} />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 w-full text-center">
        {title && <Title>Get In Touch</Title>}

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 mt-12 items-stretch">
          {/* Map */}
          <div className="rounded-2xl overflow-hidden border border-primary-border h-full min-h-[280px] lg:min-h-[300px] relative group" data-aos="fade-right" data-aos-duration="800">
            <div className="absolute inset-0 bg-gradient-to-br from-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10 pointer-events-none rounded-2xl" />
            <iframe
              width="100%"
              height="100%"
              frameBorder="0"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3818.6167683421654!2d96.11638601077851!3d16.84535751812661!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x30c194e78f6e08c5%3A0xe5631ecb38f4d774!2sTan%20Lann%20Bus%20Stop!5e0!3m2!1sen!2smm!4v1711808391142!5m2!1sen!2smm"
              allowFullScreen
              loading="lazy"
              title="Location map — Tan Lann Bus Stop, Myanmar"
              className="grayscale hover:grayscale-0 transition-all duration-500"
            ></iframe>
          </div>

          {/* Contact Form */}
          <div
            className="p-6 lg:p-8 glass-card rounded-2xl hover-glow"
            data-aos="fade-left"
            data-aos-duration="800"
          >
            <form onSubmit={sendMessage} className="space-y-5">
              {/* Name Field */}
              <div className={`floating-label relative flex items-center border rounded-xl p-3 transition-all duration-300 ${
                focusedField === 'name'
                  ? 'border-accent/60 bg-primary-card shadow-inner-glow'
                  : 'border-primary-border bg-primary-card/50'
              }`}>
                <FaUser className={`flex-shrink-0 transition-colors duration-300 ${
                  focusedField === 'name' ? 'text-accent' : 'text-accent/50'
                }`} />
                <label htmlFor="contact-name" className="sr-only">Name</label>
                <input
                  id="contact-name"
                  type="text"
                  name="user_name"
                  placeholder="Your Name"
                  className="outline-none flex-grow px-3 bg-transparent text-white placeholder-gray-dark"
                  value={info?.user_name}
                  onChange={(e) => setInfo({ ...info, user_name: e.target.value })}
                  onFocus={() => setFocusedField('name')}
                  onBlur={() => setFocusedField(null)}
                  required
                />
              </div>

              {/* Email Field */}
              <div className={`floating-label relative flex items-center border rounded-xl p-3 transition-all duration-300 ${
                focusedField === 'email'
                  ? 'border-accent/60 bg-primary-card shadow-inner-glow'
                  : 'border-primary-border bg-primary-card/50'
              }`}>
                <MdEmail className={`flex-shrink-0 transition-colors duration-300 ${
                  focusedField === 'email' ? 'text-accent' : 'text-accent/50'
                }`} />
                <label htmlFor="contact-email" className="sr-only">Email</label>
                <input
                  id="contact-email"
                  type="email"
                  name="user_email"
                  placeholder="Your Email"
                  className="outline-none flex-grow px-3 bg-transparent text-white placeholder-gray-dark"
                  value={info?.user_email}
                  onChange={(e) => setInfo({ ...info, user_email: e.target.value })}
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField(null)}
                  required
                />
              </div>

              {/* Message Field */}
              <div className={`floating-label relative flex items-start border rounded-xl p-3 transition-all duration-300 ${
                focusedField === 'message'
                  ? 'border-accent/60 bg-primary-card shadow-inner-glow'
                  : 'border-primary-border bg-primary-card/50'
              }`}>
                <label htmlFor="contact-message" className="sr-only">Message</label>
                <textarea
                  id="contact-message"
                  rows="5"
                  placeholder="Your Message"
                  name="user_message"
                  className="outline-none flex-grow px-3 bg-transparent text-white placeholder-gray-dark resize-none"
                  value={info?.user_message}
                  onChange={(e) => setInfo({ ...info, user_message: e.target.value })}
                  onFocus={() => setFocusedField('message')}
                  onBlur={() => setFocusedField(null)}
                  required
                ></textarea>
              </div>

              <Button
                type="submit"
                className={`w-full py-3.5 font-semibold text-center justify-center rounded-xl transition-all duration-500 ripple-container relative overflow-hidden ${
                  submitted
                    ? 'bg-green-500 text-white shadow-green-500/30'
                    : 'bg-accent text-primary-bg hover:bg-accent-dark shadow-accent hover:shadow-accent-lg'
                } ${isSubmitting ? 'opacity-80 cursor-wait' : ''}`}
                disabled={isSubmitting}
              >
                <span className={`transition-all duration-300 ${isSubmitting ? 'opacity-0' : 'opacity-100'}`}>
                  {submitted ? '✓ Message Sent!' : 'Send Message'}
                </span>
                {isSubmitting && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-primary-bg border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Contact
